import { PUBLIC_HOST, PUBLIC_ORIGIN } from "../lib/domain";
/**
 * ADMIN — Académie / Formations en ligne.
 * Gère les prix des produits (par discipline + pack) et les programmes d'entraînement 20/30/45 min.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { BeltEvaluationsAdmin } from "../components/academy/BeltEvaluationsAdmin";
import {
  GraduationCap, Plus, Trash, FloppyDisk, CircleNotch, Barbell, VideoCamera,
  EnvelopeSimple, PaperPlaneTilt, ChartLineUp, ShareNetwork, Ticket,
} from "@phosphor-icons/react";

const DURATIONS = [20, 30, 45];

export default function AcademieAdminModule() {
  const [meta, setMeta] = useState(null);
  const [products, setProducts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [overview, setOverview] = useState(null);
  const [savingPrices, setSavingPrices] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [m, s, p, o] = await Promise.all([
        api.get("/academy/meta"),
        api.get("/academy/settings"),
        api.get("/academy/programs"),
        api.get("/academy/admin/overview"),
      ]);
      setMeta(m.data);
      setProducts(s.data.products);
      setPrograms(p.data.programs);
      setOverview(o.data);
    } catch (e) {
      toast.error("Erreur de chargement de l'Académie.");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function savePrices() {
    setSavingPrices(true);
    try {
      await api.put("/academy/settings", { products });
      toast.success("Prix enregistrés.");
    } catch (e) {
      toast.error("Impossible d'enregistrer les prix.");
    } finally { setSavingPrices(false); }
  }

  async function deleteProgram(id) {
    if (!window.confirm("Supprimer ce programme ?")) return;
    try {
      await api.delete(`/academy/programs/${id}`);
      toast.success("Programme supprimé.");
      loadAll();
    } catch (e) { toast.error("Suppression impossible."); }
  }

  if (!meta) {
    return <div className="p-8"><CircleNotch size={30} className="animate-spin text-[#C9A961]" /></div>;
  }

  const discName = (code) => (meta.disciplines.find((d) => d.code === code)?.name) || code;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="academie-admin-module">
      <div className="flex items-center gap-3 mb-1">
        <GraduationCap size={26} weight="fill" className="text-[#C9A961]" />
        <h1 className="text-2xl font-bold text-white">Académie · Formations en ligne</h1>
      </div>
      <p className="text-white/50 text-sm mb-6">Gère les tarifs d'accès et les programmes d'entraînement des 3 disciplines.</p>

      {/* 🔗 Accès rapide — toutes les pages liées à l'Académie, au même endroit */}
      <section className="border border-white/10 bg-white/[0.02] p-5 mb-8" data-testid="academy-hub">
        <h2 className="text-white font-semibold mb-1 flex items-center gap-2">🔗 Accès rapide · Pages liées à l'Académie</h2>
        <p className="text-white/40 text-xs mb-4">Membres, rétention, tarifs, programmes, vidéos et carnets — regroupés ici.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
          {[
            { to: "/app/academie-kaizen", label: "👥 Membres Académie (gérer / modifier)", testid: "hub-membres" },
            { to: "/app/membres-paiements", label: "💳 Paiements & comptes membres", testid: "hub-paiements" },
            { to: "/app/facturation-membres", label: "🧾 Facturation membres", testid: "hub-facturation" },
            { to: "/app/audit-membres", label: "🔍 Audit membres", testid: "hub-audit" },
            { to: "/app/retention", label: "📈 Tableau de rétention (complet)", testid: "hub-retention" },
            { to: "/app/carnet-progression", label: "📓 Carnets de progression", testid: "hub-carnet" },
            { to: "/app/couverture-curriculum", label: "🗺️ Couverture du curriculum", testid: "hub-curriculum" },
            { to: "/app/videos-techniques", label: "🎬 Vidéos techniques", testid: "hub-videos" },
            { to: "/app/activation-membres", label: "🔑 Activation membres", testid: "hub-activation" },
          ].map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid}
              className="text-sm text-white/80 bg-white/[0.03] hover:bg-[#C9A961]/10 hover:text-white border border-white/8 hover:border-[#C9A961]/40 rounded-lg px-3 py-2.5 transition-colors">
              {l.label}
            </Link>
          ))}
        </div>
      </section>

      {/* Tableau de bord de rétention */}
      <RetentionDashboard />

      {/* 🥋 Élèves en ligne : évaluations de ceinture + répétitions */}
      <BeltEvaluationsAdmin />

      {/* 👤 Éditeur de membre Académie (en ligne) */}
      <MemberEditor disciplines={meta.disciplines} />


      {/* Partage + période d'essai abonnement */}
      <ShareAndTrial />

      {/* Suivi des conversions par code promo */}
      <PromoStats />

      {/* KPI */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Kpi label="Abonnés payants" value={overview.paid_subscribers} />
          <Kpi label="Programmes" value={overview.programs_total} sub={`dont ${overview.competition_programs || 0} compét.`} />
          <Kpi label="Vidéos liées" value={Object.values(overview.videos_by_discipline || {}).reduce((a, b) => a + b, 0)} />
          {meta.disciplines.map((d) => (
            <Kpi key={d.code} label={d.name} value={overview.programs_by_discipline[d.code] || 0} sub={`${overview.videos_by_discipline?.[d.code] || 0} vidéos`} />
          ))}
        </div>
      )}

      {/* PRIX */}
      <section className="border border-white/10 bg-white/[0.02] p-5 mb-8">
        <h2 className="text-white font-semibold mb-4 flex items-center gap-2"><Barbell size={18} className="text-[#C9A961]" /> Tarifs d'abonnement</h2>
        <div className="space-y-3">
          {products.map((p, i) => (
            <div key={p.code} className="flex items-center gap-3" data-testid={`academy-product-${p.code}`}>
              <span className="flex-1 text-white/80 text-sm">{p.name}</span>
              <div className="flex items-center gap-1">
                <input type="number" step="0.01" min="0" value={p.price_cad}
                  data-testid={`academy-price-${p.code}`}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value) || 0;
                    setProducts((prev) => prev.map((x, j) => j === i ? { ...x, price_cad: v } : x));
                  }}
                  className="w-24 bg-black/40 border border-white/15 text-white text-sm px-2 py-1.5 text-right" />
                <span className="text-white/40 text-sm">$/mois</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button onClick={savePrices} disabled={savingPrices} data-testid="academy-save-prices"
            className="inline-flex items-center gap-2 bg-[#C9A961] text-black font-semibold text-sm px-4 py-2.5 hover:opacity-90 disabled:opacity-50">
            {savingPrices ? <CircleNotch size={15} className="animate-spin" /> : <FloppyDisk size={15} />} Enregistrer les prix
          </button>
          <button onClick={async () => { try { const r = await api.get("/ops/academy-structure/matrix.csv", { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "matrice_membres_structure_academique.csv"; a.click(); toast.success("Matrice téléchargée (lecture seule)"); } catch { toast.error("Export impossible"); } }}
            data-testid="academy-structure-matrix-csv" title="Audit structure académique — lecture seule : membre → ceinture → groupe cible → abonnement → droits acquis → risques"
            className="inline-flex items-center gap-2 border border-white/15 text-white/80 text-sm px-4 py-2.5 hover:bg-white/5">⬇️ Matrice membres — audit structure (CSV)</button>
        </div>
      </section>

      {/* PROGRAMMES */}
      <section className="border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-semibold flex items-center gap-2"><Barbell size={18} className="text-[#C9A961]" /> Programmes d'entraînement</h2>
          <button onClick={() => setShowForm(true)} data-testid="academy-new-program-btn"
            className="inline-flex items-center gap-2 border border-[#C9A961]/50 text-[#C9A961] text-sm px-3 py-2 hover:bg-[#C9A961]/10">
            <Plus size={15} /> Nouveau programme
          </button>
        </div>
        {programs.length === 0 && <p className="text-white/40 text-sm">Aucun programme. Ajoute-en un pour l'afficher aux membres.</p>}
        <div className="space-y-2">
          {programs.map((p) => (
            <div key={p.id} className="flex items-center gap-3 border border-white/8 bg-white/[0.02] px-4 py-2.5" data-testid={`academy-program-row-${p.id}`}>
              <span className="kz-mono text-[10px] text-[#C9A961] w-14">{p.duration_min} min</span>
              <span className="flex-1 text-white/85 text-sm">{p.title}</span>
              <span className="text-white/40 text-xs">{discName(p.discipline)}</span>
              <span className="text-white/40 text-xs">{p.level || "—"}</span>
              <button onClick={() => deleteProgram(p.id)} data-testid={`academy-delete-program-${p.id}`} className="text-white/40 hover:text-red-400"><Trash size={15} /></button>
            </div>
          ))}
        </div>
      </section>

      {showForm && (
        <ProgramForm meta={meta} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); loadAll(); }} />
      )}

      {/* PHASE 2 — Gestion des vidéos du curriculum */}
      <VideoManager meta={meta} onChanged={loadAll} />

      {/* Rétention — relance automatique des abonnés inactifs */}
      <ReengagementManager />
    </div>
  );
}

function ReengagementManager() {
  const [s, setS] = useState(null);
  const [inactive, setInactive] = useState([]);
  const [count, setCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/academy/reengagement");
      setS(r.data.settings);
      setInactive(r.data.inactive || []);
      setCount(r.data.inactive_count || 0);
    } catch (e) { toast.error("Chargement de la relance impossible."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function save(next) {
    setSaving(true);
    try {
      const r = await api.put("/academy/reengagement", next);
      setS(r.data.settings);
      toast.success("Réglages de relance enregistrés.");
      load();
    } catch (e) { toast.error("Enregistrement impossible."); }
    finally { setSaving(false); }
  }

  async function sendNow() {
    if (!window.confirm(`Envoyer la relance maintenant à ${count} abonné(s) inactif(s) ?`)) return;
    setSending(true);
    try {
      const r = await api.post("/academy/reengagement/run");
      toast.success(`${r.data.sent} courriel(s) de relance envoyé(s).`);
      load();
    } catch (e) { toast.error("Envoi impossible."); }
    finally { setSending(false); }
  }

  if (!s) return null;
  return (
    <section className="border border-white/10 bg-white/[0.02] p-5 mt-8" data-testid="academy-reengagement">
      <h2 className="text-white font-semibold mb-1 flex items-center gap-2"><EnvelopeSimple size={18} className="text-[#C9A961]" /> Relance automatique des abonnés inactifs</h2>
      <p className="text-white/45 text-xs mb-4">Courriel automatique aux abonnés qui ne se sont pas entraînés depuis un moment (« Ça fait X jours — reprends ta progression »). Réduit les désabonnements.</p>

      <div className="flex flex-wrap items-end gap-4 mb-4">
        <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer" data-testid="reengage-enabled-label">
          <input type="checkbox" checked={s.enabled} data-testid="reengage-enabled"
            onChange={(e) => save({ ...s, enabled: e.target.checked })} />
          Activer l'envoi automatique quotidien
        </label>
        <div>
          <label className="block text-white/50 text-xs mb-1">Inactif après (jours)</label>
          <input type="number" min="1" value={s.inactivity_days} data-testid="reengage-inactivity"
            onChange={(e) => setS({ ...s, inactivity_days: parseInt(e.target.value) || 10 })}
            className="w-20 bg-black/40 border border-white/15 text-white text-sm px-2 py-1.5" />
        </div>
        <div>
          <label className="block text-white/50 text-xs mb-1">Pause entre relances (jours)</label>
          <input type="number" min="1" value={s.cooldown_days} data-testid="reengage-cooldown"
            onChange={(e) => setS({ ...s, cooldown_days: parseInt(e.target.value) || 14 })}
            className="w-20 bg-black/40 border border-white/15 text-white text-sm px-2 py-1.5" />
        </div>
        <button onClick={() => save(s)} disabled={saving} data-testid="reengage-save"
          className="inline-flex items-center gap-2 border border-white/15 text-white/80 text-sm px-3 py-2 hover:bg-white/[0.05] disabled:opacity-50">
          {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} />} Enregistrer
        </button>
      </div>

      <div className="flex items-center justify-between border border-white/10 bg-white/[0.02] px-4 py-3">
        <span className="text-white/80 text-sm" data-testid="reengage-count">
          <b className="text-[#C9A961]">{count}</b> abonné(s) inactif(s) actuellement
        </span>
        <button onClick={sendNow} disabled={sending || count === 0} data-testid="reengage-send-now"
          className="inline-flex items-center gap-2 bg-[#C9A961] text-black font-semibold text-sm px-4 py-2 hover:opacity-90 disabled:opacity-40">
          {sending ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />} Envoyer maintenant
        </button>
      </div>
      {inactive.length > 0 && (
        <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
          {inactive.map((t) => (
            <div key={t.email} className="flex items-center gap-3 text-xs text-white/60 px-3 py-1.5 border border-white/8" data-testid={`reengage-target-${t.email}`}>
              <span className="flex-1 truncate">{t.email}</span>
              <span className="text-white/40">{t.days_inactive} j inactif</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function VideoManager({ meta, onChanged }) {
  const [discipline, setDiscipline] = useState(meta.disciplines[0].code);
  const [belt, setBelt] = useState("");
  const [belts, setBelts] = useState([]);
  const [techs, setTechs] = useState([]);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/academy/admin/curriculum", {
        params: { discipline, belt: belt || undefined, only_missing: onlyMissing },
      });
      setTechs(r.data.techniques);
      setBelts(r.data.belts);
      const d = {};
      r.data.techniques.forEach((t) => { d[t.id] = t.video_url || ""; });
      setDrafts(d);
    } catch (e) { toast.error("Chargement des techniques impossible."); }
    finally { setLoading(false); }
  }, [discipline, belt, onlyMissing]);

  useEffect(() => { load(); }, [load]);

  async function saveVideo(id) {
    try {
      await api.put(`/academy/admin/curriculum/${id}/video`, { video_url: drafts[id] || "" });
      toast.success("Vidéo enregistrée.");
      setTechs((prev) => prev.map((t) => t.id === id ? { ...t, video_url: drafts[id] } : t));
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible."); }
  }

  return (
    <section className="border border-white/10 bg-white/[0.02] p-5 mt-8" data-testid="academy-video-manager">
      <h2 className="text-white font-semibold mb-1 flex items-center gap-2"><VideoCamera size={18} className="text-[#C9A961]" /> Vidéos des techniques</h2>
      <p className="text-white/45 text-xs mb-4">Colle une URL YouTube <b>non répertoriée</b> par technique. Seuls les membres avec accès voient la vidéo.</p>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-white/50 text-xs mb-1">Discipline</label>
          <select value={discipline} onChange={(e) => { setDiscipline(e.target.value); setBelt(""); }} data-testid="vm-discipline"
            className="bg-black/40 border border-white/15 text-white text-sm px-2 py-2">
            {meta.disciplines.map((d) => <option key={d.code} value={d.code} className="bg-[#0C0A09]">{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-white/50 text-xs mb-1">Ceinture</label>
          <select value={belt} onChange={(e) => setBelt(e.target.value)} data-testid="vm-belt"
            className="bg-black/40 border border-white/15 text-white text-sm px-2 py-2">
            <option value="" className="bg-[#0C0A09]">Toutes</option>
            {belts.map((b) => <option key={b} value={b} className="bg-[#0C0A09]">{b}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-white/60 text-xs cursor-pointer pb-2">
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} data-testid="vm-only-missing" />
          Sans vidéo seulement
        </label>
      </div>
      {loading ? <CircleNotch size={22} className="animate-spin text-[#C9A961]" /> : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {techs.length === 0 && <p className="text-white/40 text-sm">Aucune technique.</p>}
          {techs.map((t) => (
            <div key={t.id} className="flex items-center gap-2 border border-white/8 px-3 py-2" data-testid={`vm-tech-${t.id}`}>
              <span className="kz-mono text-[10px] text-white/40 w-24 shrink-0 truncate">{t.belt}</span>
              <span className="text-white/80 text-sm w-40 shrink-0 truncate">{t.name}</span>
              <input value={drafts[t.id] || ""} placeholder="URL YouTube…"
                onChange={(e) => setDrafts((p) => ({ ...p, [t.id]: e.target.value }))}
                data-testid={`vm-input-${t.id}`}
                className="flex-1 bg-black/40 border border-white/15 text-white text-xs px-2 py-1.5" />
              <button onClick={() => saveVideo(t.id)} data-testid={`vm-save-${t.id}`}
                className="text-[#C9A961] hover:opacity-80 shrink-0"><FloppyDisk size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ShareAndTrial() {
  const url = `${PUBLIC_ORIGIN}/academie`;
  const [trial, setTrial] = useState(7);
  const [banner, setBanner] = useState({ enabled: true, code: "PREMIERE", text: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/academy/general-settings").then((r) => {
      setTrial(r.data.settings.trial_days);
      if (r.data.settings.promo_banner) setBanner(r.data.settings.promo_banner);
    }).catch(() => {});
  }, []);

  async function saveTrial() {
    setSaving(true);
    try {
      await api.put("/academy/general-settings", {
        trial_days: trial,
        promo_banner: { ...banner, code: (banner.code || "").toUpperCase() },
      });
      toast.success("Réglages enregistrés.");
    } catch (e) { toast.error("Enregistrement impossible."); }
    finally { setSaving(false); }
  }

  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copié !`); };
  const captionIG = `🥋 Du dojo à la scène internationale. Techniques, programmes 20/30/45 min et carnet de progression en Kickboxing/Muay Thai, Jiu-jitsu Can Ryu et BJJ. Abonne-toi 👉 ${PUBLIC_HOST}/academie`;
  const captionFB = `Nouvelle Académie en ligne de l'Institut Impact Élite Martial 🥋 Suis ta progression, revois tes techniques et entraîne-toi avec des programmes structurés dans 3 disciplines. Découvre-la 👉 ${PUBLIC_HOST}/academie`;
  const bio = `🥋 Académie en ligne · ${PUBLIC_HOST}/academie`;

  return (
    <section className="border border-white/10 bg-white/[0.02] p-5 mb-8" data-testid="academy-share">
      <h2 className="text-white font-semibold mb-4 flex items-center gap-2"><ShareNetwork size={18} className="text-[#C9A961]" /> Partager & vendre l'Académie</h2>
      <div className="grid md:grid-cols-[auto_1fr] gap-6">
        <div className="text-center">
          <img src="/academie-qr.png" alt="QR Académie" className="w-40 h-40 bg-white p-2 mx-auto" data-testid="academy-qr" />
          <a href="/academie-qr.png" download="academie-qr.png" className="inline-block mt-2 text-[#C9A961] text-xs hover:underline" data-testid="academy-qr-download">Télécharger le QR</a>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input readOnly value={url} className="flex-1 bg-black/40 border border-white/15 text-white text-sm px-3 py-2" data-testid="share-link" />
            <button onClick={() => copy(url, "Lien")} className="border border-white/15 text-white/80 text-sm px-3 py-2 hover:bg-white/[0.05]" data-testid="share-copy-link">Copier</button>
          </div>
          <ShareCopy label="Légende Instagram" text={captionIG} onCopy={copy} testid="share-ig" />
          <ShareCopy label="Publication Facebook" text={captionFB} onCopy={copy} testid="share-fb" />
          <ShareCopy label="Bio (courte)" text={bio} onCopy={copy} testid="share-bio" />
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mt-6 pt-5 border-t border-white/8">
        <div>
          <label className="block text-white/50 text-xs mb-1">🎁 Période d'essai abonnement en ligne (jours) — 0 = désactivé</label>
          <input type="number" min="0" value={trial} onChange={(e) => setTrial(parseInt(e.target.value) || 0)} data-testid="trial-days"
            className="w-24 bg-black/40 border border-white/15 text-white text-sm px-2 py-1.5" />
        </div>
        <button onClick={saveTrial} disabled={saving} data-testid="trial-save"
          className="inline-flex items-center gap-2 bg-[#C9A961] text-black font-semibold text-sm px-4 py-2 hover:opacity-90 disabled:opacity-50">
          {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} />} Enregistrer
        </button>
        <span className="text-white/40 text-xs pb-2">La période d'essai ne s'affiche qu'en juillet/août (saisonnier).</span>
      </div>

      {/* Bannière promo affichée sur /academie */}
      <div className="mt-5 pt-5 border-t border-white/8" data-testid="promo-banner-settings">
        <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer mb-3">
          <input type="checkbox" checked={!!banner.enabled} data-testid="promo-banner-enabled"
            onChange={(e) => setBanner({ ...banner, enabled: e.target.checked })} />
          🎟️ Afficher la bannière promo sur la page de vente
        </label>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-white/50 text-xs mb-1">Code promo</label>
            <input value={banner.code || ""} data-testid="promo-banner-code-input"
              onChange={(e) => setBanner({ ...banner, code: e.target.value.toUpperCase() })}
              className="w-32 bg-black/40 border border-white/15 text-white text-sm px-2 py-1.5 kz-mono tracking-wider" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <label className="block text-white/50 text-xs mb-1">Texte de la bannière</label>
            <input value={banner.text || ""} data-testid="promo-banner-text-input"
              onChange={(e) => setBanner({ ...banner, text: e.target.value })}
              className="w-full bg-black/40 border border-white/15 text-white text-sm px-2 py-1.5" />
          </div>
        </div>
        <p className="text-white/40 text-xs mt-2">
          ⚠️ Le code doit exister dans Stripe (ex. PREMIERE, ETE2026) pour être accepté au paiement.
        </p>
      </div>
    </section>
  );
}

function PromoStats() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/academy/admin/promo-stats").then((r) => setD(r.data)).catch(() => {});
  }, []);
  if (!d) return null;
  const fmtDate = (ts) => ts ? new Date(ts * 1000).toLocaleDateString("fr-CA") : "—";
  return (
    <section className="border border-white/10 bg-white/[0.02] p-5 mb-8" data-testid="promo-stats">
      <h2 className="text-white font-semibold mb-1 flex items-center gap-2"><Ticket size={18} className="text-[#C9A961]" /> Conversions par code promo</h2>
      <p className="text-white/45 text-xs mb-4">
        Utilisations comptées par Stripe · revenu attribué aux abonnés Académie ayant utilisé le code.
        {d.stripe_ok === false && <span className="text-amber-400"> (Stripe indisponible — compteurs partiels)</span>}
      </p>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Kpi label="Utilisations totales" value={d.total_redeemed || 0} />
        <Kpi label="Revenu mensuel attribué" value={`${(d.total_attributed_mrr_cad || 0).toFixed(2)} $`} />
      </div>
      {(!d.codes || d.codes.length === 0) ? (
        <p className="text-white/40 text-sm">Aucun code promo de campagne trouvé pour l'instant.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="promo-stats-table">
            <thead>
              <tr className="text-left text-white/40 text-xs border-b border-white/10">
                <th className="py-2 pr-3">Code</th>
                <th className="py-2 pr-3">Réduction</th>
                <th className="py-2 pr-3">Utilisations</th>
                <th className="py-2 pr-3">Abonnés attribués</th>
                <th className="py-2 pr-3">Revenu / mois</th>
                <th className="py-2 pr-3">Statut</th>
                <th className="py-2">Expire</th>
              </tr>
            </thead>
            <tbody>
              {d.codes.map((c) => (
                <tr key={c.code} className="border-b border-white/5" data-testid={`promo-row-${c.code}`}>
                  <td className="py-2 pr-3 kz-mono text-[#C9A961] tracking-wider">{c.code}</td>
                  <td className="py-2 pr-3 text-white/70">{c.percent_off != null ? `-${c.percent_off}%` : "—"}</td>
                  <td className="py-2 pr-3 text-white font-semibold">{c.times_redeemed != null ? c.times_redeemed : "—"}</td>
                  <td className="py-2 pr-3 text-white/70">{c.attributed_subscribers}</td>
                  <td className="py-2 pr-3 text-white/70">{c.attributed_mrr_cad.toFixed(2)} $</td>
                  <td className="py-2 pr-3">
                    {c.active === true ? <span className="text-green-400 text-xs">actif</span>
                      : c.active === false ? <span className="text-white/40 text-xs">inactif</span>
                      : <span className="text-white/30 text-xs">—</span>}
                  </td>
                  <td className="py-2 text-white/50 text-xs">{fmtDate(c.expires_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}



function ShareCopy({ label, text, onCopy, testid }) {
  return (
    <div className="border border-white/8 bg-white/[0.02] p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="kz-mono text-[10px] tracking-wider text-[#C9A961] uppercase">{label}</span>
        <button onClick={() => onCopy(text, label)} className="text-white/60 hover:text-white text-xs" data-testid={`${testid}-copy`}>Copier</button>
      </div>
      <p className="text-white/60 text-xs leading-relaxed">{text}</p>
    </div>
  );
}

function RetentionDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/academy/retention/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, []);
  if (!d) return null;
  const activePct = d.total_subscribers ? Math.round(d.active / d.total_subscribers * 100) : 0;
  return (
    <section className="border border-[#C9A961]/30 bg-gradient-to-br from-[#C9A961]/[0.05] to-transparent p-5 mb-8" data-testid="retention-dashboard">
      <h2 className="text-white font-semibold mb-4 flex items-center gap-2"><ChartLineUp size={18} className="text-[#C9A961]" /> Tableau de bord de rétention</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Abonnés payants" value={d.total_subscribers} />
        <Kpi label="Actifs" value={d.active} sub={`${activePct}%`} />
        <Kpi label="Inactifs" value={d.inactive} />
        <Kpi label="Complétion vidéo moy." value={`${d.avg_completion_percent}%`} />
      </div>
      {d.total_subscribers > 0 && (
        <div className="mb-4" data-testid="retention-active-bar">
          <div className="flex justify-between kz-mono text-[10px] text-white/50 mb-1">
            <span>Actifs vs inactifs</span><span>{d.active} actifs · {d.inactive} inactifs</span>
          </div>
          <div className="h-3 bg-red-500/30 overflow-hidden flex">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${activePct}%` }} />
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-white/10 bg-white/[0.02] p-4">
          <div className="text-2xl font-bold text-white">{d.reengagement.emails_sent}</div>
          <div className="text-white/50 text-xs">Relances envoyées {d.reengagement.enabled ? "· auto activé ✅" : "· auto désactivé"}</div>
        </div>
        <div className="border border-green-500/30 bg-green-500/[0.04] p-4" data-testid="retention-reactivations">
          <div className="text-2xl font-bold text-green-400">{d.reengagement.reactivations}</div>
          <div className="text-white/50 text-xs">Reprises après relance (désabonnements évités)</div>
        </div>
      </div>
    </section>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-4">
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-white/50 text-xs">{label}{sub ? ` · ${sub}` : ""}</div>
    </div>
  );
}

function ProgramForm({ meta, onClose, onSaved }) {
  const beltsByDisc = meta.belts_by_discipline || {};
  const firstDisc = meta.disciplines[0].code;
  const [f, setF] = useState({
    discipline: firstDisc, title: "", duration_min: 20, level: (beltsByDisc[firstDisc] || [])[0] || "",
    objective: "", warmup: "", technical_block: "", conditioning: "", cooldown: "", equipment: "",
    video_url: "", is_competition: false,
  });
  const [busy, setBusy] = useState(false);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const beltOpts = beltsByDisc[f.discipline] || [];

  // Réaligne la ceinture sur l'échelle de la discipline choisie.
  useEffect(() => {
    setF((p) => (beltOpts.includes(p.level) ? p : { ...p, level: beltOpts[0] || "" }));
  }, [f.discipline]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    if (!f.title.trim()) { toast.error("Titre requis."); return; }
    setBusy(true);
    try {
      await api.post("/academy/programs", f);
      toast.success("Programme créé.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création impossible.");
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[120] bg-black/70 flex items-start justify-center overflow-y-auto p-4" data-testid="academy-program-form">
      <div className="w-full max-w-lg bg-[#0C0A09] border border-white/15 my-8 p-5">
        <h3 className="text-white font-semibold mb-4">Nouveau programme d'entraînement</h3>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <Sel label="Discipline" value={f.discipline} onChange={(v) => up("discipline", v)} testid="pf-discipline"
            opts={meta.disciplines.map((d) => ({ v: d.code, l: d.name }))} />
          <Sel label="Durée" value={f.duration_min} onChange={(v) => up("duration_min", parseInt(v))} testid="pf-duration"
            opts={DURATIONS.map((d) => ({ v: d, l: `${d} min` }))} />
          <Sel label="Ceinture" value={f.level} onChange={(v) => up("level", v)} testid="pf-level"
            opts={beltOpts.map((b) => ({ v: b, l: b }))} />
        </div>
        <Inp label="Titre" value={f.title} onChange={(v) => up("title", v)} testid="pf-title" />
        <Inp label="Objectif" value={f.objective} onChange={(v) => up("objective", v)} testid="pf-objective" />
        <Inp label="Échauffement" value={f.warmup} onChange={(v) => up("warmup", v)} />
        <Inp label="Bloc technique" value={f.technical_block} onChange={(v) => up("technical_block", v)} />
        <Inp label="Conditionnement" value={f.conditioning} onChange={(v) => up("conditioning", v)} />
        <Inp label="Retour au calme" value={f.cooldown} onChange={(v) => up("cooldown", v)} />
        <Inp label="Équipement" value={f.equipment} onChange={(v) => up("equipment", v)} />
        <Inp label="Vidéo YouTube (non répertoriée) — URL ou ID" value={f.video_url} onChange={(v) => up("video_url", v)} testid="pf-video" />
        <label className="flex items-center gap-2 mt-2 text-white/70 text-sm cursor-pointer" data-testid="pf-competition-label">
          <input type="checkbox" checked={f.is_competition} onChange={(e) => up("is_competition", e.target.checked)} data-testid="pf-competition" />
          Programme de compétition
        </label>
        <div className="flex gap-2 mt-4">
          <button onClick={save} disabled={busy} data-testid="pf-save"
            className="flex-1 bg-[#C9A961] text-black font-semibold py-2.5 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {busy ? <CircleNotch size={15} className="animate-spin" /> : "Créer le programme"}
          </button>
          <button onClick={onClose} className="border border-white/15 text-white/70 px-4 py-2.5">Annuler</button>
        </div>
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, testid }) {
  return (
    <div className="mb-2">
      <label className="block text-white/50 text-xs mb-1">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-black/40 border border-white/15 text-white text-sm px-3 py-2" />
    </div>
  );
}

function Sel({ label, value, onChange, opts, testid }) {
  return (
    <div>
      <label className="block text-white/50 text-xs mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-black/40 border border-white/15 text-white text-sm px-2 py-2">
        {opts.map((o) => <option key={o.v} value={o.v} className="bg-[#0C0A09]">{o.l}</option>)}
      </select>
    </div>
  );
}



// 👤 Éditeur de membre Académie — modifier accès/statut/courriel, offrir 1 mois, renvoyer lien
function MemberEditor({ disciplines }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null); // membre sélectionné (email)
  const [busy, setBusy] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/academy/admin/members");
      setMembers(data.members || []);
    } catch (e) { toast.error("Chargement des membres Académie échoué."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = members.find((m) => m.email === sel) || null;

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action impossible."); }
    finally { setBusy(false); }
  };

  const toggleDiscipline = (code) => {
    if (!selected) return;
    const cur = new Set(selected.unlocked || []);
    if (cur.has(code)) cur.delete(code); else cur.add(code);
    act(() => api.post("/academy/admin/members/access", { email: selected.email, disciplines: [...cur], full_name: selected.full_name }),
      "Accès mis à jour.");
  };

  const filtered = members.filter((m) =>
    !q || (m.email + " " + (m.full_name || "")).toLowerCase().includes(q.toLowerCase()));

  return (
    <section className="border border-white/10 bg-white/[0.02] p-5 mb-8" data-testid="academy-member-editor">
      <h2 className="text-white font-semibold mb-1 flex items-center gap-2">👤 Éditeur de membre Académie (en ligne)</h2>
      <p className="text-white/40 text-xs mb-4">Modifie l'accès aux 3 disciplines, le statut, le courriel · offre 1 mois gratuit · renvoie le lien de connexion.</p>

      <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="member-editor-search"
        placeholder="Rechercher un membre (nom ou courriel)…"
        className="w-full bg-black/40 border border-white/15 text-white text-sm px-3 py-2 mb-3 rounded-md" />

      {loading ? (
        <div className="text-white/40 text-sm py-4"><CircleNotch size={18} className="animate-spin inline" /> Chargement…</div>
      ) : members.length === 0 ? (
        <p className="text-white/40 text-sm py-2">Aucun abonné Académie en ligne pour l'instant.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Liste */}
          <div className="max-h-80 overflow-y-auto border border-white/8 rounded-lg divide-y divide-white/5" data-testid="member-editor-list">
            {filtered.map((m) => (
              <button key={m.email} onClick={() => setSel(m.email)} data-testid={`member-row-${m.email}`}
                className={`w-full text-left px-3 py-2.5 hover:bg-white/[0.04] transition ${sel === m.email ? "bg-[#C9A961]/10" : ""}`}>
                <div className="text-sm text-white/90 truncate">{m.full_name || m.email}</div>
                <div className="text-[11px] text-white/45 truncate">{m.email}</div>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${m.payment_status === "paid" ? "bg-emerald-900/50 text-emerald-200" : m.payment_status === "suspended" ? "bg-red-900/50 text-red-200" : "bg-zinc-800 text-zinc-300"}`}>
                    {m.payment_status === "paid" ? "Actif" : m.payment_status === "suspended" ? "Suspendu" : "En attente"}
                  </span>
                  <span className="text-[10px] text-white/40">{(m.unlocked || []).length}/3 disciplines</span>
                  {m.is_member && <span className="text-[10px] text-[#C9A961]">membre dojo</span>}
                </div>
              </button>
            ))}
          </div>

          {/* Détail / actions */}
          <div className="border border-white/8 rounded-lg p-4" data-testid="member-editor-detail">
            {!selected ? (
              <p className="text-white/40 text-sm">Sélectionne un membre pour le modifier.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="text-white font-medium">{selected.full_name || "—"}</div>
                  <div className="text-white/50 text-xs">{selected.email}</div>
                  {selected.free_until && <div className="text-[11px] text-amber-300 mt-1">🎁 Gratuit jusqu'au {String(selected.free_until).slice(0, 10)}</div>}
                  {selected.is_member && <div className="text-[11px] text-[#C9A961] mt-1">Accès inclus (membre du dojo actif)</div>}
                </div>

                <div>
                  <div className="text-white/60 text-xs mb-1.5">Accès aux disciplines</div>
                  <div className="space-y-1.5">
                    {disciplines.map((d) => {
                      const on = (selected.unlocked || []).includes(d.code);
                      return (
                        <button key={d.code} disabled={busy || selected.is_member} onClick={() => toggleDiscipline(d.code)}
                          data-testid={`member-toggle-${d.code}`}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-md border text-sm transition disabled:opacity-50 ${on ? "border-emerald-600/50 bg-emerald-900/20 text-emerald-100" : "border-white/12 bg-white/[0.02] text-white/70"}`}>
                          <span>{d.name}</span>
                          <span className={`text-[11px] px-2 py-0.5 rounded-full ${on ? "bg-emerald-600/30 text-emerald-100" : "bg-white/8 text-white/50"}`}>{on ? "✓ débloqué" : "verrouillé"}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button disabled={busy} data-testid="member-free-month"
                    onClick={() => act(() => api.post("/academy/admin/members/free-month", { email: selected.email }), "1 mois gratuit offert.")}
                    className="text-xs px-3 py-2 rounded-md border border-amber-600/50 bg-amber-900/20 text-amber-200 hover:bg-amber-900/40 disabled:opacity-50">🎁 Offrir 1 mois</button>
                  {selected.payment_status === "suspended" ? (
                    <button disabled={busy} data-testid="member-reactivate"
                      onClick={() => act(() => api.post("/academy/admin/members/reactivate", { email: selected.email }), "Accès réactivé.")}
                      className="text-xs px-3 py-2 rounded-md border border-emerald-600/50 bg-emerald-900/20 text-emerald-200 hover:bg-emerald-900/40 disabled:opacity-50">✅ Réactiver</button>
                  ) : (
                    <button disabled={busy} data-testid="member-suspend"
                      onClick={() => act(() => api.post("/academy/admin/members/suspend", { email: selected.email }), "Accès suspendu.")}
                      className="text-xs px-3 py-2 rounded-md border border-red-600/50 bg-red-900/20 text-red-200 hover:bg-red-900/40 disabled:opacity-50">⛔ Suspendre</button>
                  )}
                  <button disabled={busy} data-testid="member-resend-login"
                    onClick={() => act(() => api.post("/academy/admin/members/resend-login", { email: selected.email }), "Lien de connexion renvoyé.")}
                    className="text-xs px-3 py-2 rounded-md border border-white/15 bg-white/[0.02] text-white/80 hover:bg-white/[0.06] disabled:opacity-50">🔑 Renvoyer le lien</button>
                </div>

                <div>
                  <div className="text-white/60 text-xs mb-1.5">Changer le courriel</div>
                  <div className="flex gap-2">
                    <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="member-new-email"
                      placeholder="nouveau@courriel.com"
                      className="flex-1 bg-black/40 border border-white/15 text-white text-sm px-3 py-2 rounded-md" />
                    <button disabled={busy || !newEmail} data-testid="member-change-email"
                      onClick={() => act(async () => { await api.post("/academy/admin/members/change-email", { old_email: selected.email, new_email: newEmail }); setSel(newEmail.trim().toLowerCase()); setNewEmail(""); }, "Courriel modifié.")}
                      className="text-xs px-3 py-2 rounded-md bg-[#C9A961] text-black font-semibold hover:opacity-90 disabled:opacity-50">Changer</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
